import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { Document, Packer, Paragraph, TextRun, HeadingLevel, AlignmentType, Table, TableRow, TableCell, WidthType, BorderStyle } from 'docx';
import { saveAs } from 'file-saver';

/** Shared colors for PDF capture (light-theme output, diagrams). Use constants for maintainability. */
const PDF_CAPTURE_COLORS = {
  WHITE: '#ffffff',
  TEXT_DARK: '#1a1a1a',
  CODE_BG: '#f6f8fa',
  CODE_TEXT: '#24292f',
  BORDER_LIGHT: '#e8e8e8'
};

/**
 * Parse markdown into structured elements (used by DOCX; exported for unit tests).
 * @param {string} text - Raw markdown
 * @returns {Array<{type: string, content?: string, items?: string[], rows?: string[][]}>}
 */
export const parseMarkdown = (text) => {
  const lines = text.split('\n');
  const elements = [];
  let currentList = [];
  let inCodeBlock = false;
  let codeBlockContent = [];
  let tableRows = [];
  let inTable = false;

  const flushList = () => {
    if (currentList.length > 0) {
      elements.push({ type: 'list', items: currentList });
      currentList = [];
    }
  };

  let lastWasSpacer = false;
  const pushSpacer = () => {
    if (lastWasSpacer) return;
    elements.push({ type: 'spacer', height: 6 });
    lastWasSpacer = true;
  };

  lines.forEach((line) => {
    const trimmed = line.trim();

    // Code blocks
    if (trimmed.startsWith('```')) {
      flushList();
      if (inCodeBlock) {
        elements.push({ type: 'code', content: codeBlockContent.join('\n') });
        codeBlockContent = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      return;
    }

    if (inCodeBlock) {
      codeBlockContent.push(line);
      return;
    }

    // Tables
    if (trimmed.includes('|') && trimmed.split('|').length > 2) {
      if (!inTable) {
        flushList();
        inTable = true;
      }
      const cells = trimmed.split('|').map(c => c.trim()).filter(c => c && !c.match(/^[-:]+$/));
      if (cells.length > 0) {
        tableRows.push(cells);
      }
      return;
    } else if (inTable) {
      elements.push({ type: 'table', rows: tableRows });
      tableRows = [];
      inTable = false;
    }

    // Empty line: flush list and add spacer so sections are separated
    if (!trimmed) {
      flushList();
      pushSpacer();
      return;
    }
    lastWasSpacer = false;

    // Headers (## and ### with optional leading emoji/spaces)
    if (trimmed.startsWith('### ')) {
      flushList();
      elements.push({ type: 'h3', content: trimmed.slice(4) });
      return;
    }
    if (trimmed.startsWith('## ')) {
      flushList();
      elements.push({ type: 'h2', content: trimmed.slice(3) });
      return;
    }
    if (trimmed.startsWith('# ')) {
      flushList();
      elements.push({ type: 'h1', content: trimmed.slice(2) });
      return;
    }

    // Unordered list: - or *
    if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
      flushList();
      currentList.push(trimmed.slice(2));
      return;
    }

    // Regular paragraph (includes numbered lines like "1. text" or "**1. text**")
    flushList();
    elements.push({ type: 'paragraph', content: trimmed });
  });

  flushList();
  if (inTable && tableRows.length > 0) {
    elements.push({ type: 'table', rows: tableRows });
  }
  if (inCodeBlock && codeBlockContent.length > 0) {
    elements.push({ type: 'code', content: codeBlockContent.join('\n') });
  }

  return elements;
};

/**
 * Strip markdown formatting to plain text (used by PDF text fallback and DOCX; exported for unit tests).
 * @param {string} text
 * @returns {string}
 */
export const cleanMarkdown = (text) => {
  return text
    .replace(/\*\*(.+?)\*\*/g, '$1')  // Bold
    .replace(/\*(.+?)\*/g, '$1')      // Italic
    .replace(/`(.+?)`/g, '$1');       // Inline code
};

// --- Emoji support for PDF (jsPDF default fonts don't render emojis) ---
// Set to true to render emojis as small inline images; false to replace with text (cleaner layout, no images)
const USE_EMOJI_IMAGES = true;

// Match emoji/symbols: symbol ranges + surrogate pairs for U+1F300–1F9FF (no \p{} for broad support)
const EMOJI_REGEX = /([\u2600-\u26FF\u2700-\u27BF]|\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDFFF]|\uD83E[\uDC00-\uDFFF])(\uFE0F|\u200D[\u2600-\u27BF\uD83C-\uD83D\uD83E\uDC00-\uDFFF]*)*/g;

// Alternative: replace emojis with plain-text equivalents for a clean PDF with no images (USE_EMOJI_IMAGES = false)
const EMOJI_TO_TEXT = {
  '✅': '[OK]', '✓': '[OK]', '✔': '[OK]', '☑': '[OK]',
  '❌': '[X]', '✗': '[X]', '✔️': '[OK]', '❎': '[X]',
  '⚠️': '[!]', '⚠': '[!]', '❗': '[!]', '❕': '[!]',
  'ℹ️': '[i]', 'ℹ': '[i]', '🔵': '[*]', '🟢': '[*]', '🟡': '[*]', '🔴': '[*]',
  '👉': '->', '←': '<-', '→': '->', '↑': '^', '↓': 'v',
  '⭐': '*', '🌟': '*', '💡': '[i]', '📌': '-', '📋': '-',
  '👍': '+', '👎': '-', '🙂': ':)', '😀': ':)', '😊': ':)', '😅': ':)', '😂': ':)',
  '📊': '[chart]', '📈': '[up]', '📉': '[down]', '🔒': '[lock]', '🔓': '[unlock]',
};
function replaceEmojisWithText(str) {
  if (!str) return str;
  EMOJI_REGEX.lastIndex = 0;
  return str.replace(EMOJI_REGEX, (m) => EMOJI_TO_TEXT[m] || EMOJI_TO_TEXT[m.replace(/\uFE0F/g, '')] || '[+]');
}

/** Splits a string into text and emoji segments (exported for unit tests). */
export function splitIntoTextAndEmoji(str) {
  if (!str || typeof str !== 'string') return [{ type: 'text', value: '' }];
  const segments = [];
  let lastEnd = 0;
  let m;
  EMOJI_REGEX.lastIndex = 0;
  while ((m = EMOJI_REGEX.exec(str)) !== null) {
    if (m.index > lastEnd) {
      segments.push({ type: 'text', value: str.slice(lastEnd, m.index) });
    }
    segments.push({ type: 'emoji', value: m[0] });
    lastEnd = m.index + m[0].length;
  }
  if (lastEnd < str.length) {
    segments.push({ type: 'text', value: str.slice(lastEnd) });
  }
  if (segments.length === 0) segments.push({ type: 'text', value: str });
  return segments;
}

function emojiToImageDataUrl(emoji, fontSizePx = 12) {
  const size = Math.max(16, Math.round(fontSizePx * 1.15));
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  ctx.font = `${size}px "Segoe UI Emoji", "Apple Color Emoji", "Noto Color Emoji", sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(emoji, size / 2, size / 2);
  try {
    return { dataUrl: canvas.toDataURL('image/png'), size };
  } catch (e) {
    return null;
  }
}

/**
 * Add text that may contain emojis to the PDF. Renders text with doc.text() and emojis as small images.
 * Returns the new y position.
 */
function addTextWithEmojis(doc, text, xStart, yStart, maxWidth, lineHeight, fontSize, ensureSpace) {
  if (!USE_EMOJI_IMAGES) text = replaceEmojisWithText(text);
  const segments = splitIntoTextAndEmoji(text);
  let x = xStart;
  let y = yStart;
  /* Emoji size in pt: ~38% of font size so emojis sit on the text baseline; min 4pt for visibility. */
  const emojiSizePt = Math.max(4, fontSize * 0.38);
  const emojiVerticalOffset = 0.5;   /* pt: nudge emoji down to align with text baseline */
  const textPaddingPt = 1.2;         /* pt: space between emoji and following text */
  const emojiGapPt = fontSize * 0.18; /* pt: gap after emoji before next segment */
  const hasEmoji = segments.some((s) => s.type === 'emoji');
  if (!hasEmoji) {
    const lines = doc.splitTextToSize(text, maxWidth);
    lines.forEach((line) => {
      ensureSpace(lineHeight);
      doc.text(line, xStart, y);
      y += lineHeight;
    });
    return y;
  }
  segments.forEach((seg) => {
    if (seg.type === 'text') {
      if (!seg.value) return;
      const availableWidth = Math.max(5, xStart + maxWidth - x - textPaddingPt);
      const firstLineMax = x > xStart ? availableWidth : maxWidth;
      const allFirst = doc.splitTextToSize(seg.value, firstLineMax);
      const firstLine = allFirst[0] || '';
      const rest = firstLine.length < seg.value.length ? seg.value.slice(firstLine.length).trimStart() : '';
      const restLines = rest ? doc.splitTextToSize(rest, maxWidth - textPaddingPt) : [];
      const lines = [firstLine, ...restLines].filter(Boolean);
      lines.forEach((line, i) => {
        const w = doc.getStringUnitWidth(line);
        const fitsOnLine = (x + w + textPaddingPt) <= (xStart + maxWidth);
        if (!fitsOnLine && x > xStart) {
          x = xStart;
          y += lineHeight;
          ensureSpace(lineHeight);
        }
        ensureSpace(lineHeight);
        doc.text(line, x, y);
        x += w + textPaddingPt;
        if (i < lines.length - 1) {
          x = xStart;
          y += lineHeight;
        }
      });
    } else {
      const img = emojiToImageDataUrl(seg.value, fontSize * 2);
      if (img && img.dataUrl) {
        const w = emojiSizePt;
        const h = emojiSizePt;
        const totalW = w + emojiGapPt;
        const fitsOnLine = (x + totalW) <= (xStart + maxWidth);
        if (!fitsOnLine && x > xStart) {
          x = xStart;
          y += lineHeight;
          ensureSpace(lineHeight);
        }
        ensureSpace(lineHeight);
        try {
          doc.addImage(img.dataUrl, 'PNG', x, y - h * emojiVerticalOffset, w, h);
        } catch (err) {
          doc.text(seg.value, x, y);
        }
        x += totalW;
      } else {
        const w = doc.getStringUnitWidth(seg.value);
        doc.text(seg.value, x, y);
        x += w + textPaddingPt;
      }
    }
  });
  return y + lineHeight;
}

// ==================== MERMAID SVG → IMAGE (for PDF capture) ====================

/**
 * Get width and height for an SVG (from viewBox or width/height attributes). Exported for unit tests.
 * @param {SVGSVGElement} svg
 * @returns {{ width: number, height: number }}
 */
export function getSvgDimensions(svg) {
  const w = svg.getAttribute('width');
  const h = svg.getAttribute('height');
  const viewBox = svg.getAttribute('viewBox');
  if (w != null && h != null) {
    const nw = parseFloat(w);
    const nh = parseFloat(h);
    if (!Number.isNaN(nw) && !Number.isNaN(nh) && nw > 0 && nh > 0) {
      return { width: nw, height: nh };
    }
  }
  if (viewBox) {
    const parts = viewBox.trim().split(/\s+/);
    if (parts.length >= 4) {
      const vw = parseFloat(parts[2]);
      const vh = parseFloat(parts[3]);
      if (!Number.isNaN(vw) && !Number.isNaN(vh) && vw > 0 && vh > 0) {
        return { width: vw, height: vh };
      }
    }
  }
  return { width: 800, height: 600 };
}

/**
 * Force diagram background to white so the PNG/PDF doesn't have a black/dark background.
 * Mermaid (and similar) often add a full-size rect with dark fill; we set it to white.
 * @param {SVGSVGElement} svgEl
 */
function forceMermaidSvgWhiteBackground(svgEl) {
  const { width, height } = getSvgDimensions(svgEl);
  const viewBox = svgEl.getAttribute('viewBox');
  let vw = width;
  let vh = height;
  if (viewBox) {
    const parts = viewBox.trim().split(/\s+/);
    if (parts.length >= 4) {
      vw = parseFloat(parts[2]);
      vh = parseFloat(parts[3]);
    }
  }
  svgEl.querySelectorAll('rect').forEach((rect) => {
    const rw = parseFloat(rect.getAttribute('width'));
    const rh = parseFloat(rect.getAttribute('height'));
    if (!Number.isNaN(rw) && !Number.isNaN(rh) && rw >= vw * 0.9 && rh >= vh * 0.9) {
      rect.setAttribute('fill', PDF_CAPTURE_COLORS.WHITE);
      rect.style.fill = PDF_CAPTURE_COLORS.WHITE;
    }
  });
  svgEl.style.backgroundColor = PDF_CAPTURE_COLORS.WHITE;
}

/**
 * Convert an SVG element to a PNG data URL so html2canvas can capture it.
 * html2canvas does not reliably render dynamically injected SVGs; rasterizing fixes that.
 * @param {SVGSVGElement} svgEl
 * @returns {Promise<string>} PNG data URL
 */
function svgToPngDataUrl(svgEl) {
  return new Promise((resolve, reject) => {
    forceMermaidSvgWhiteBackground(svgEl);
    const { width, height } = getSvgDimensions(svgEl);
    /* scale 2: 2x device pixel ratio for sharp text/graphics in the rasterized PNG. */
    const scale = 2;
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);
    const svgString = new XMLSerializer().serializeToString(svgEl);
    const svgBlob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    const img = new Image();
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = PDF_CAPTURE_COLORS.WHITE;
        ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, width, height, 0, 0, w, h);
        const dataUrl = canvas.toDataURL('image/png');
        URL.revokeObjectURL(url);
        resolve(dataUrl);
      } catch (e) {
        URL.revokeObjectURL(url);
        reject(e);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load SVG as image'));
    };
    img.src = url;
  });
}

/**
 * Replace all Mermaid diagram SVGs inside a container with PNG images.
 * Call this on a clone of the message content before html2canvas so diagrams render in the PDF.
 * @param {HTMLElement} container - Cloned message content element (not the live DOM)
 * @returns {Promise<void>}
 */
async function replaceMermaidSvgsWithImages(container) {
  const mermaidContainers = container.querySelectorAll('.ai-mermaid-container');
  const promises = [];
  const replacements = [];
  mermaidContainers.forEach((el) => {
    const svg = el.querySelector('svg');
    if (!svg) return;
    promises.push(
      svgToPngDataUrl(svg).then((dataUrl) => {
        replacements.push({ el, dataUrl });
      }).catch((err) => {
        console.warn('PDF: Mermaid SVG to PNG conversion failed, diagram may not render in PDF', err);
        // Leave container as-is so html2canvas can still attempt to capture the SVG
      })
    );
  });
  await Promise.all(promises);
  replacements.forEach(({ el, dataUrl }) => {
    const img = document.createElement('img');
    img.src = dataUrl;
    img.style.maxWidth = '100%';
    img.style.height = 'auto';
    img.style.display = 'block';
    img.alt = 'Mermaid diagram';
    el.innerHTML = '';
    el.appendChild(img);
  });
}

/**
 * Force light-theme colors on a cloned content root so the PDF is readable.
 * Overrides inline styles from dark mode (React often sets color via style={{ color: ... }}).
 * @param {HTMLElement} root - Cloned message content element (inside .pdf-capture-light)
 */
function applyLightThemeToClone(root) {
  const { WHITE, TEXT_DARK, CODE_BG, CODE_TEXT, BORDER_LIGHT } = PDF_CAPTURE_COLORS;

  root.style.setProperty('background-color', WHITE, 'important');
  root.style.setProperty('color', TEXT_DARK, 'important');

  const all = root.querySelectorAll('*');
  all.forEach((el) => {
    el.style.setProperty('color', TEXT_DARK, 'important');
  });

  const codeSelectors = '.ai-markdown-code-block, .ai-markdown-code-header, .ai-markdown-code-pre, pre, code';
  root.querySelectorAll(codeSelectors).forEach((el) => {
    el.style.setProperty('background-color', CODE_BG, 'important');
    el.style.setProperty('color', CODE_TEXT, 'important');
    el.style.setProperty('border-color', BORDER_LIGHT, 'important');
  });

  root.querySelectorAll('.ai-markdown-heading-1, .ai-markdown-heading-2, .ai-markdown-heading-3, .ai-markdown-heading-4').forEach((el) => {
    el.style.setProperty('color', TEXT_DARK, 'important');
  });
}

// ==================== PDF GENERATION ====================

/** Content height (px) above which we use scale 1.5 to reduce memory (canvas ~width*scale*height*scale*4 bytes). */
const LARGE_CONTENT_HEIGHT_PX = 4000;
/** Content height (px) above which we skip DOM capture and use text-only fallback to avoid very large canvas. */
const EXTREME_CONTENT_HEIGHT_PX = 10000;

/**
 * Generate PDF report.
 * Response section uses DOM capture only: options.contentElement (the rendered message div) is
 * captured with html2canvas so the PDF shows exactly what you see on the AI assistant page.
 * @param {string} question - User question
 * @param {string} _response - Kept for backward compatibility; response content comes from options.contentElement (DOM capture), not from this argument.
 * @param {object} metadata - Optional metadata
 * @param {object} options - options.contentElement (DOM node) is required for the response; pass the ref to the message content div.
 * @param {function(string): void} [options.onWarning] - Called with a message when using reduced resolution or text-only fallback for long content.
 * @param {function(Error): void} [options.onError] - Called on failure; if provided, used instead of alert() for user-friendly notification.
 * @returns {Promise<{ success: true } | { success: false, error: Error }>}
 */
export const generatePDF = async (question, _response, metadata = {}, options = {}) => {
  try {
    const doc = new jsPDF();

    // --- Layout Context ---
    const margin = { top: 20, bottom: 20, left: 14, right: 14 };
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPos = margin.top;

    const ensureSpace = (need = 20) => {
      if (yPos + need > pageHeight - margin.bottom) {
        doc.addPage();
        yPos = margin.top;
      }
    };

    const setBodyFont = () => {
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(10);
      doc.setTextColor(0,0,0);
    };

    // --- HEADER ---
    doc.setFillColor(24, 144, 255);
    doc.rect(0, 0, pageWidth, 30, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(255, 255, 255);
    doc.text('ClairAI - Clairai-Copilot Report', 14, 18);
    yPos = 40;

    // --- METADATA ---
    doc.setFont('helvetica','normal');
    doc.setFontSize(9);
    doc.setTextColor(100,100,100);

    doc.text(`Generated: ${new Date().toLocaleString()}`, margin.left, yPos);
    yPos += 5;
    if (metadata.model) {
      doc.text(`Model: ${metadata.model}`, margin.left, yPos);
      yPos += 5;
    }
    if (metadata.account) {
      doc.text(`Account: ${metadata.account}`, margin.left, yPos);
      yPos += 5;
    }
    yPos += 8;

    // --- QUESTION ---
    const boxWidth = pageWidth - margin.left - margin.right;
    doc.setFillColor(230, 247, 255);
    doc.rect(margin.left, yPos - 5, boxWidth, 20, 'F');

    doc.setFont('helvetica','bold');
    doc.setFontSize(11);
    doc.setTextColor(0,0,0);
    doc.text('Question:', margin.left + 4, yPos);

    yPos += 7;
    setBodyFont();
    yPos = addTextWithEmojis(doc, question, margin.left + 4, yPos, boxWidth - 6, 5, 10, ensureSpace);
    yPos += 10;

    // --- RESPONSE ---
    doc.setFont('helvetica','bold');
    doc.setFontSize(11);
    doc.text('Response:', margin.left, yPos);
    yPos += 8;

    let responseCapturedFromDOM = false;
    let responseSkippedForLength = false;
    const contentElement = options.contentElement;
    const onWarning = typeof options.onWarning === 'function' ? options.onWarning : () => {};

    if (contentElement && typeof contentElement.getBoundingClientRect === 'function') {
      const contentHeight = contentElement.offsetHeight || 0;
      if (contentHeight > EXTREME_CONTENT_HEIGHT_PX) {
        responseSkippedForLength = true;
        onWarning('Content is very long; using text-only output to avoid memory issues.');
      }
      if (!responseSkippedForLength) {
        try {
          /* Use 1.5 for large content to reduce canvas memory; 2 for normal content for sharper output. */
          const scale = contentHeight > LARGE_CONTENT_HEIGHT_PX ? 1.5 : 2;
          if (contentHeight > LARGE_CONTENT_HEIGHT_PX) {
            onWarning('Long content: using reduced resolution to avoid high memory use.');
          }
          // Clone so we don't mutate the live UI. Mermaid diagrams use SVG via dangerouslySetInnerHTML,
          // which html2canvas often fails to capture; replace SVGs with PNG images in the clone first.
          const clone = contentElement.cloneNode(true);
          const width = contentElement.offsetWidth || 600;
          clone.classList.add('pdf-capture-mode');
          clone.style.backgroundColor = PDF_CAPTURE_COLORS.WHITE;
          // Wrap in light-theme container so dark mode styles are overridden for readable PDF output
          const wrapper = document.createElement('div');
          wrapper.className = 'pdf-capture-light';
          wrapper.setAttribute('data-theme', 'light');
          wrapper.style.position = 'absolute';
          wrapper.style.left = '-9999px';
          wrapper.style.top = '0';
          wrapper.style.width = `${width}px`;
          wrapper.style.backgroundColor = PDF_CAPTURE_COLORS.WHITE;
          wrapper.appendChild(clone);
          try {
            document.body.appendChild(wrapper);
            applyLightThemeToClone(clone);
            await replaceMermaidSvgsWithImages(clone);
            const canvas = await html2canvas(clone, {
              scale,
              useCORS: true,
              logging: false,
              backgroundColor: PDF_CAPTURE_COLORS.WHITE
            });
          const imgData = canvas.toDataURL('image/png');
          const imgW = boxWidth;
          const imgH = (canvas.height * imgW) / canvas.width;
          const getAvailableHeight = () => pageHeight - margin.bottom - yPos;
          const pageHeightAvailable = getAvailableHeight();
          if (imgH <= pageHeightAvailable) {
            doc.addImage(imgData, 'PNG', margin.left, yPos, imgW, imgH);
            yPos += imgH;
          } else {
            let remaining = imgH;
            let sourceY = 0;
            const sourceHeight = canvas.height;
            const sourceWidth = canvas.width;
            while (remaining > 0) {
              const available = getAvailableHeight();
              const chunkH = Math.min(available, (sourceHeight * available) / imgH);
              const sourceChunkHeight = (chunkH / imgH) * sourceHeight;
              const sliceCanvas = document.createElement('canvas');
              sliceCanvas.width = sourceWidth;
              sliceCanvas.height = sourceChunkHeight;
              const ctx = sliceCanvas.getContext('2d');
              ctx.drawImage(canvas, 0, sourceY, sourceWidth, sourceChunkHeight, 0, 0, sourceWidth, sourceChunkHeight);
              const sliceData = sliceCanvas.toDataURL('image/png');
              doc.addImage(sliceData, 'PNG', margin.left, yPos, imgW, chunkH);
              remaining -= chunkH;
              sourceY += sourceChunkHeight;
              if (remaining > 0) {
                doc.addPage();
                yPos = margin.top;
              } else {
                yPos += chunkH;
              }
            }
          }
          responseCapturedFromDOM = true;
        } finally {
          if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
        }
        } catch (err) {
          console.warn('PDF: capture response from DOM failed', err);
        }
      }
    }

    if (!responseCapturedFromDOM) {
      setBodyFont();
      if (responseSkippedForLength && _response) {
        // Text-only fallback for extremely long content to avoid high memory use
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        yPos = addTextWithEmojis(doc, cleanMarkdown(_response), margin.left, yPos, boxWidth, 5, 9, ensureSpace);
      } else {
        doc.setFontSize(9);
        doc.setTextColor(120, 120, 120);
        const msg = contentElement
          ? 'Response capture failed. Please try again.'
          : 'Response is captured from the chat view. Please download from the AI assistant page with the message visible.';
        const msgLines = doc.splitTextToSize(msg, boxWidth);
        msgLines.forEach((line) => {
          ensureSpace(5);
          doc.text(line, margin.left, yPos);
          yPos += 5;
        });
        doc.setTextColor(0, 0, 0);
      }
    }

    // --- FOOTER ---
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setTextColor(150,150,150);
      doc.text(
        `Page ${i} of ${totalPages} | ClairAI Report | ${new Date().toLocaleDateString()}`,
        pageWidth / 2,
        pageHeight - 10,
        { align: 'center' }
      );
    }

    doc.save(`ClairAI_Report_${Date.now()}.pdf`);
    return { success: true };
  } catch (err) {
    console.error('PDF Generation Error:', err);
    const onError = typeof options.onError === 'function' ? options.onError : null;
    if (onError) {
      onError(err);
    } else {
      alert('Failed to generate PDF. Please try again.');
    }
    return { success: false, error: err };
  }
};

// ==================== DOCX GENERATION ====================
export const generateDOCX = async (question, response, metadata = {}) => {
  const elements = parseMarkdown(response);
  const docChildren = [];

  // Header
  docChildren.push(
    new Paragraph({
      text: 'ClairAI - Clairai-Copilot Report',
      heading: HeadingLevel.HEADING_1,
      alignment: AlignmentType.CENTER,
      spacing: { after: 200 }
    })
  );

  // Metadata
  docChildren.push(
    new Paragraph({
      children: [
        new TextRun({
          text: `Generated: ${new Date().toLocaleString()}`,
          size: 18,
          color: '666666'
        })
      ],
      spacing: { after: 100 }
    })
  );

  if (metadata.model) {
    docChildren.push(
      new Paragraph({
        children: [
          new TextRun({
            text: `Model: ${metadata.model}`,
            size: 18,
            color: '666666'
          })
        ],
        spacing: { after: 100 }
      })
    );
  }

  // Question
  docChildren.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'Question',
          bold: true,
          size: 24
        })
      ],
      spacing: { before: 200, after: 100 }
    })
  );

  docChildren.push(
    new Paragraph({
      text: question,
      spacing: { after: 300 },
      shading: { fill: 'E6F7FF' }
    })
  );

  // Response
  docChildren.push(
    new Paragraph({
      children: [
        new TextRun({
          text: 'Response',
          bold: true,
          size: 24
        })
      ],
      spacing: { before: 200, after: 200 }
    })
  );

  // Render elements (DOCX only; PDF uses DOM capture or addTextWithEmojis fallback)
  elements.forEach((element) => {
    switch (element.type) {
      case 'h1':
        docChildren.push(
          new Paragraph({
            text: cleanMarkdown(element.content),
            heading: HeadingLevel.HEADING_1,
            spacing: { before: 200, after: 100 }
          })
        );
        break;

      case 'h2':
        docChildren.push(
          new Paragraph({
            text: cleanMarkdown(element.content),
            heading: HeadingLevel.HEADING_2,
            spacing: { before: 200, after: 100 }
          })
        );
        break;

      case 'h3':
        docChildren.push(
          new Paragraph({
            text: cleanMarkdown(element.content),
            heading: HeadingLevel.HEADING_3,
            spacing: { before: 150, after: 100 }
          })
        );
        break;

      case 'paragraph':
        docChildren.push(
          new Paragraph({
            text: cleanMarkdown(element.content),
            spacing: { after: 100 }
          })
        );
        break;

      case 'list':
        element.items.forEach((item) => {
          docChildren.push(
            new Paragraph({
              text: cleanMarkdown(item),
              bullet: { level: 0 },
              spacing: { after: 50 }
            })
          );
        });
        docChildren.push(new Paragraph({ text: '', spacing: { after: 100 } }));
        break;

      case 'code':
        docChildren.push(
          new Paragraph({
            children: [
              new TextRun({
                text: element.content,
                font: 'Courier New',
                size: 18
              })
            ],
            shading: { fill: 'F6F8FA' },
            spacing: { before: 100, after: 100 }
          })
        );
        break;

      case 'table':
        if (element.rows.length > 0) {
          const tableRows = element.rows.map((row, idx) =>
            new TableRow({
              children: row.map(
                (cell) =>
                  new TableCell({
                    children: [new Paragraph(cleanMarkdown(cell))],
                    shading: idx === 0 ? { fill: '1890FF' } : undefined
                  })
              )
            })
          );

          docChildren.push(
            new Table({
              rows: tableRows,
              width: { size: 100, type: WidthType.PERCENTAGE },
              borders: {
                top: { style: BorderStyle.SINGLE, size: 1 },
                bottom: { style: BorderStyle.SINGLE, size: 1 },
                left: { style: BorderStyle.SINGLE, size: 1 },
                right: { style: BorderStyle.SINGLE, size: 1 }
              }
            })
          );

          docChildren.push(new Paragraph({ text: '', spacing: { after: 200 } }));
        }
        break;

      default:
        break;
    }
  });

  // Create document
  const doc = new Document({
    sections: [
      {
        properties: {},
        children: docChildren
      }
    ]
  });

  // Generate and save
  const blob = await Packer.toBlob(doc);
  saveAs(blob, `ClairAI_Report_${Date.now()}.docx`);
};